import { useEffect, useMemo, useRef, useState } from "react";
import { Building, ChevronDown, Share2 } from "lucide-react";
import { useCondos } from "context/CondosContext";
import { useManagers } from "context/ManagersContext";
import { useAuth } from "context/AuthContext";
import { ModeSelector } from "components/ModeSelector";
import { ShareDialog } from "components/ShareDialog";
import { SearchBox } from "components/ui/SearchBox";
import Spinner from "components/ui/Spinner";
import { formatTimestamp } from "utils/dateFormat";
import { contactListItemClasses } from "utils/contactListItemClasses";
import { Condo } from "types/condo";

/** Show the per-section search only once the list is long enough to need it. */
const SEARCH_MIN_ITEMS = 7;

const EMPTY_BACKGROUND = "#F3F4F6";

interface CondoSwitcherProps {
  onSelectCondo: (condoId: number) => void;
}

// Map light colors to their stronger variants for the dot
function getStrongerColor(lightColor: string): string {
  const colorMap: Record<string, string> = {
    "#DBEAFE": "#3B82F6", // Light Blue -> Blue
    "#D1FAE5": "#10B981", // Light Green -> Green
    "#FEF3C7": "#F59E0B", // Light Amber -> Amber
    "#FEE2E2": "#EF4444", // Light Red -> Red
    "#E9D5FF": "#8B5CF6", // Light Purple -> Purple
    "#FCE7F3": "#EC4899", // Light Pink -> Pink
    "#CFFAFE": "#06B6D4", // Light Cyan -> Cyan
    "#CCFBF1": "#14B8A6", // Light Teal -> Teal
    "#FFEDD5": "#F97316", // Light Orange -> Orange
    "#E0E7FF": "#6366F1", // Light Indigo -> Indigo
    "#ECFCCB": "#84CC16", // Light Lime -> Lime
    "#F3E8FF": "#A855F7", // Light Violet -> Violet
  };
  return colorMap[lightColor] || lightColor;
}

/** Dark text on light backgrounds, light text on dark ones. */
function textColorFor(backgroundColor: string): string {
  const rgb = parseInt(backgroundColor.slice(1), 16);
  if (Number.isNaN(rgb)) {
    return "text-gray-900";
  }
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = (rgb >> 0) & 0xff;
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? "text-gray-900" : "text-white";
}

// Unread first, then most recent activity first
function byUnreadThenRecent(a: Condo, b: Condo): number {
  if (a.hasUnread && !b.hasUnread) return -1;
  if (!a.hasUnread && b.hasUnread) return 1;

  if (a.lastMessageAt && b.lastMessageAt) {
    return (
      new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
    );
  }
  if (a.lastMessageAt) return -1;
  if (b.lastMessageAt) return 1;

  return 0;
}

/**
 * Scope selector for the Home sidebar: shows the building everything below is
 * scoped to, and switches it. Rendered above the contact lists rather than
 * alongside them so the hierarchy reads correctly.
 */
export function CondoSwitcher({ onSelectCondo }: CondoSwitcherProps) {
  const { condos, loading, error, selectedCondoId, refresh } = useCondos();
  const { managers } = useManagers();
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setQuery("");
    }
  }, [open]);

  const selectedCondo = condos.find((c) => c.id === selectedCondoId) ?? null;

  const sortedCondos = useMemo(
    () => [...condos].sort(byUnreadThenRecent),
    [condos],
  );

  const filteredCondos = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return sortedCondos;
    return sortedCondos.filter((condo) =>
      condo.name.toLowerCase().includes(needle),
    );
  }, [sortedCondos, query]);

  // Unread elsewhere: the signal an always-visible building list used to carry.
  const otherUnreadCount = useMemo(
    () => condos.filter((c) => c.hasUnread && c.id !== selectedCondoId).length,
    [condos, selectedCondoId],
  );

  const backgroundColor =
    selectedCondo?.color || (selectedCondo ? "#DBEAFE" : EMPTY_BACKGROUND);
  const textColor = textColorFor(backgroundColor);

  const isActive = selectedCondo?.mode === "live";
  const propertyManagerName =
    managers.length > 0 ? managers[0].propertyManagerName : undefined;

  const triggerLabel = selectedCondo
    ? selectedCondo.name
    : locale === "es"
      ? "Seleccionar edificio"
      : "Select a building";

  const unreadTitle =
    otherUnreadCount === 1
      ? locale === "es"
        ? "1 edificio con mensajes sin leer"
        : "1 building with unread messages"
      : locale === "es"
        ? `${otherUnreadCount} edificios con mensajes sin leer`
        : `${otherUnreadCount} buildings with unread messages`;

  const handleSelect = (condoId: number) => {
    onSelectCondo(condoId);
    setOpen(false);
  };

  return (
    <>
      {/* Divider marks the scope boundary: everything below is inside this
          building. Matches the bottom edge of an expanded ContactList so the
          sidebar keeps one vertical rhythm. */}
      <div ref={rootRef} className="border-b border-gray-200 pb-4">
        <div
          className="relative z-40 rounded-lg border border-gray-200 p-3"
          style={{ backgroundColor }}
        >
          <div className="space-y-3">
            {selectedCondo && (
              <ModeSelector
                condoId={selectedCondo.id}
                currentMode={selectedCondo.mode}
                unitsCount={selectedCondo.unitsCount}
                accountAlreadyLive={condos.some((condo) => condo.mode === "live")}
                onModeChange={() => {
                  void refresh();
                }}
              />
            )}
            {/* The name owns its own row: it is the longest, least truncatable
                thing in the card, and the switcher target should be full width. */}
            <button
              type="button"
              onClick={() => setOpen((prev) => !prev)}
              aria-haspopup="listbox"
              aria-expanded={open}
              className="flex w-full items-center gap-1.5 rounded text-left"
              title={
                locale === "es" ? "Cambiar de edificio" : "Switch building"
              }
            >
              <Building
                className={`h-4 w-4 flex-shrink-0 ${textColor} opacity-70`}
              />
              <span
                className={`min-w-0 flex-1 truncate text-sm font-semibold ${textColor}`}
              >
                {triggerLabel}
              </span>
              {otherUnreadCount > 0 && (
                <span
                  title={unreadTitle}
                  className="flex-shrink-0 rounded-full bg-blue-600 px-1.5 py-0.5 text-xs font-semibold leading-none text-white"
                >
                  {otherUnreadCount}
                </span>
              )}
              <ChevronDown
                className={`h-4 w-4 flex-shrink-0 ${textColor} transition-transform ${
                  open ? "rotate-180" : ""
                }`}
              />
            </button>
            {isActive && (
              <button
                onClick={() => setShareDialogOpen(true)}
                className="flex w-full items-center justify-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                title={locale === "es" ? "Compartir Eloria" : "Share Eloria"}
              >
                <Share2 className="h-4 w-4" />
                <span>{locale === "es" ? "Compartir" : "Share"}</span>
              </button>
            )}
          </div>

          {open && (
            <div
              role="listbox"
              className="absolute left-0 right-0 top-full mt-1 max-h-72 overflow-y-auto rounded border border-gray-200 bg-white p-2 shadow-lg"
            >
              {loading && (
                <div className="flex justify-center py-6" aria-busy="true">
                  <Spinner />
                </div>
              )}
              {!loading && error && (
                <div className="p-2 text-xs text-red-600">{error}</div>
              )}
              {!loading && !error && (
                <>
                  {condos.length >= SEARCH_MIN_ITEMS && (
                    <SearchBox
                      value={query}
                      onChange={setQuery}
                      placeholder={
                        locale === "es"
                          ? "Buscar edificios..."
                          : "Search buildings..."
                      }
                    />
                  )}
                  {filteredCondos.length === 0 ? (
                    <div className="p-2 text-xs text-gray-500">
                      {condos.length === 0
                        ? locale === "es"
                          ? "Aún no hay edificios."
                          : "No buildings yet."
                        : locale === "es"
                          ? "Sin resultados."
                          : "No results."}
                    </div>
                  ) : (
                    <ul className="space-y-1">
                      {filteredCondos.map((condo) => {
                        const isSelected = condo.id === selectedCondoId;
                        return (
                          <li
                            key={condo.id}
                            role="option"
                            aria-selected={isSelected}
                            onClick={() => handleSelect(condo.id)}
                            className="cursor-pointer"
                          >
                            <div
                              className={`flex cursor-pointer items-center gap-2 ${contactListItemClasses(
                                isSelected,
                              )}`}
                            >
                              <div
                                className="h-3 w-3 flex-shrink-0 rounded-full"
                                style={{
                                  backgroundColor: getStrongerColor(
                                    condo.color,
                                  ),
                                }}
                              />
                              <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
                                <div
                                  className={`truncate text-xs lg:text-sm ${
                                    condo.hasUnread
                                      ? "font-bold"
                                      : "font-medium"
                                  }`}
                                >
                                  {condo.name}
                                </div>
                                {condo.lastMessageAt && (
                                  <div className="flex-shrink-0 text-xs text-gray-400">
                                    {formatTimestamp(
                                      condo.lastMessageAt,
                                      locale,
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {!!propertyManagerName && selectedCondo && (
        <ShareDialog
          isOpen={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          propertyManagerName={propertyManagerName}
          buildingName={selectedCondo.name}
        />
      )}
    </>
  );
}
